import React, {useEffect, useState} from "react";
import {useObserver} from "mobx-react-lite";
import {Col, Row, Form, Input, Switch} from "antd";
import {BankOutlined, CheckOutlined, CloseOutlined} from "@ant-design/icons";
import MaskedInput from "antd-mask-input";
import {useFormik, FormikProvider, getIn} from "formik";
import {useHistory} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {SingleMarkerMap} from "../../_defaultComponents/SingleMarkerMap/SingleMarkerMap";
import {updateSubCompanyPropType} from "../updateSubCompany/updateSubCompanyPropTypes";
import {updateSubCompanyValidation} from "../updateSubCompany/updateSubCompanyValidation";
import {ErrorFocus} from "../../_defaultComponents/ErrorFocus/ErrorFocus";
import {CustomButton} from "../../_defaultComponents/CustomButton/CustomButton";
import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";
import TagInput from "../../_defaultComponents/TagInput/TagInput";
import {AuthorizedIDForm} from "../../profile/AuthorizedIDForm/AuthorizedIDForm";

export const UpdateSubCompany = ({subCompanyId}) => {
    const history = useHistory();
    const stores = useStore();
    const {t} = useTranslation();
    const [companyCode, setCompanyCode] = useState();
    const [companyCityCode, setCompanyCityCode] = useState();
    const [tags, setTags] = useState([]);
    const [creditSwitch, setCreditSwitch] = useState(false);
    const [poolSwitch, setPoolSwitch] = useState(false);
    const [smartLockerSwitch, setSmartLockerSwitch] = useState(false);
    const [isAllocateClosed, setIsAllocateClosed] = useState(false);
    const [onboardingStatus, setOnboardingStatus] = useState("");
    const [isTestUser, setIsTestUser] = useState(false);

    const [loading, subCompany, setSubCompany, setLoading] = useFetch({
        fetchFn: stores.companyStore.getSubCompany.bind(null, {subCompanyId}),
        showLoading: true,
        condition: !!subCompanyId,
        callbackFunc: response => {
            if (response?.data?.isSmartLocker) {
                setSmartLockerSwitch(true);
            }
            if (response?.data?.subCompanyWorkType === "pool") {
                setPoolSwitch(true);
            }
            if (response?.data?.credit >= 0 && response?.data?.credit !== 9999999) {
                setCreditSwitch(true);
                return;
            }
            if (response?.data?.credit === 9999999) {
                setCreditSwitch(false);
            }
            if (response?.data?.onboardingStatus) {
                setOnboardingStatus(response.data.onboardingStatus);
            }
            if (response?.data?.isTestUser) {
                setIsTestUser(response.data.isTestUser);
            }
        }
    });

    /*TODO: Form component will be used in UpdateSubCompany and AddSubCompany pages.
    Code refactoring needed for blocking duplicate usage.
    */

    const formik = useFormik({
        initialValues: {
            ...subCompany?.data,
            uniqueKey: subCompany?.data?.uniqueKey || "",
            subCompanyCode: subCompany?.data?.subCompanyCode?.code,
            expCode: subCompany?.data?.expCode,
            onboardingStatus: subCompany?.data?.onboardingStatus,
            isAllocateClosed: subCompany?.data?.isAllocateClosed,
            collectionAmount: subCompany?.data?.collectionAmount,
            maxAcceptableDistance: subCompany?.data?.maxAcceptableDistance,
            category: subCompany?.data?.category,
            signName: subCompany?.data?.signName,
            externalId: subCompany?.data?.externalId,
            isTestUser: subCompany?.data?.isTestUser
        },
        validationSchema: updateSubCompanyValidation,
        enableReinitialize: true,
        onSubmit: async (values: updateSubCompanyPropType) => {
            values.authorizedPhone = values.authorizedPhone.replace(/[-,(,)]/g, "");
            values.subCompanyCode = `${companyCode}${("00" + companyCityCode).slice(-2)}${
                values.subCompanyCode
            }`;
            values.onboardingStatus = onboardingStatus;
            values.isAllocateClosed = isAllocateClosed;
            values.isTestUser = isTestUser;
            if (creditSwitch) {
                if (subCompany?.data?.credit === 9999999) {
                    values.credit = 0;
                    values.subCompanyWorkType = "dedicated";
                } else {
                    values.credit = subCompany?.data?.credit;
                }
            } else {
                values.credit = 9999999;
                values.subCompanyWorkType = "pool";
            }
            if (poolSwitch) {
                values.subCompanyWorkType = "pool";
            } else {
                values.subCompanyWorkType = "dedicated";
            }
            if (smartLockerSwitch) {
                values.isSmartLocker = true;
            } else {
                values.isSmartLocker = false;
            }
            await handleSubCompanySubmit(values);
        }
    });

    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue
    } = formik;

    const handleSubCompanySubmit = async (values: updateSubCompanyPropType) => {
        const {subCompanyCode, ...rest} = values;
        const result = await stores.companyStore.updateSubCompany(rest);
        if (result) {
            history.push("/companies/sub-company/" + subCompanyId);
        }
    };

    useEffect(() => {
        if (values.companyId && subCompany?.data?._id && !subCompany?.data?.subCompanyCode?.code) {
            stores.companyStore
                .generateSubcompanyCode({
                    companyId: values.companyId,
                    subCompanyLocation: values.location
                })
                .then(res => {
                    setCompanyCode(res.companyCode);
                    setCompanyCityCode(res.cityCode);
                    setFieldValue("subCompanyCode", ("0000" + res.code).slice(-4));
                })
                .catch(e => Sentry.captureException(e));
        } else if (subCompany?.data?.subCompanyCode?.code) {
            setCompanyCode(subCompany.data.subCompanyCode.companyCode);
            setCompanyCityCode(subCompany.data.subCompanyCode.cityCode);
            setFieldValue(
                "subCompanyCode",
                ("0000" + subCompany.data.subCompanyCode.code).slice(-4)
            );
        }
        if (subCompany.data?.allowedOrderProviders) {
            setTags(subCompany.data?.allowedOrderProviders);
        }
        if (subCompany?.data?.onboardingStatus) {
            setOnboardingStatus(subCompany.data.onboardingStatus);
        }
        if (subCompany?.data?.isAllocateClosed) {
            setIsAllocateClosed(subCompany.data.isAllocateClosed);
        } else {
            setIsAllocateClosed(false);
        }
        if (subCompany?.data?.isTestUser) {
            setIsTestUser(subCompany.data.isTestUser);
        } else {
            setIsTestUser(false);
        }
    }, [
        values.companyId,
        subCompany?.data?._id,
        subCompany?.data?.subCompanyCode?.code,
        subCompany?.data?.onboardingStatus,
        subCompany?.data?.isAllocateClosed,
        subCompany?.data?.isTestUser,
        values?.location?.longitude,
        values?.location?.latitude
    ]);

    useEffect(() => {
        setFieldValue("allowedOrderProviders", tags);
    }, [tags]);

    return useObserver(() => (
        <Col>
            <Form
                autoComplete="off"
                labelCol={{span: 24}}
                wrapperCol={{span: 24}}
                onFinish={handleSubmit}
            >
                <ErrorFocus formik={formik} />
                <FormikProvider value={formik}>
                    <Form.Item
                        label={`${t("fields.companyName", {
                            ns: "common"
                        })} / ${t("fields.branchName", {ns: "common"})}`}
                        validateStatus={
                            getIn(errors, `name`) && getIn(touched, `name`) ? "error" : "success"
                        }
                        help={
                            getIn(errors, `name`) && getIn(touched, `name`)
                                ? getIn(errors, `name`)
                                : null
                        }
                    >
                        <Input name={"name"} onChange={handleChange} value={values.name} />
                    </Form.Item>
                    <Form.Item label="Tabela Adı">
                        <Input name={"signName"} onChange={handleChange} value={values.signName} />
                    </Form.Item>
                    <Form.Item label="Firma Kategorisi">
                        <Input name={"category"} onChange={handleChange} value={values.category} />
                    </Form.Item>
                    <Form.Item
                        label="Tahsilat Miktarı"
                        validateStatus={
                            getIn(errors, `collectionAmount`) && getIn(touched, `collectionAmount`)
                                ? "error"
                                : "success"
                        }
                        help={
                            getIn(errors, `collectionAmount`) && getIn(touched, `collectionAmount`)
                                ? getIn(errors, `collectionAmount`)
                                : null
                        }
                    >
                        <Input
                            type="number"
                            name={"collectionAmount"}
                            onChange={e => {
                                const value = parseFloat(e.target.value);
                                if (value >= 0 || e.target.value === "") {
                                    handleChange(e);
                                }
                            }}
                            value={values.collectionAmount}
                            min={0}
                        />
                    </Form.Item>
                    {(stores?.meStore?.checkRole("isAdmin") ||
                        stores?.meStore?.checkRole("isOperationUser")) && (
                        <>
                            <Form.Item
                                label="Maksimum Mesafe"
                                validateStatus={
                                    getIn(errors, `maxAcceptableDistance`) &&
                                    getIn(touched, `maxAcceptableDistance`)
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    getIn(errors, `maxAcceptableDistance`) &&
                                    getIn(touched, `maxAcceptableDistance`)
                                        ? getIn(errors, `maxAcceptableDistance`)
                                        : null
                                }
                            >
                                <Input
                                    type="number"
                                    name={"maxAcceptableDistance"}
                                    onChange={e => {
                                        const value = parseFloat(e.target.value);
                                        if (value >= 0 || e.target.value === "") {
                                            handleChange(e);
                                        }
                                    }}
                                    value={values.maxAcceptableDistance}
                                    min={0}
                                />
                            </Form.Item>
                            <Form.Item
                                label="Zorunlu Kurye Bildirim Mesafesi"
                                validateStatus={
                                    getIn(errors, `forceCourierNotificationDistance`) &&
                                    getIn(touched, `forceCourierNotificationDistance`)
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    getIn(errors, `forceCourierNotificationDistance`) &&
                                    getIn(touched, `forceCourierNotificationDistance`)
                                        ? getIn(errors, `forceCourierNotificationDistance`)
                                        : null
                                }
                            >
                                <Input
                                    type="number"
                                    name={"forceCourierNotificationDistance"}
                                    onChange={e => {
                                        const value = parseFloat(e.target.value);
                                        if (value >= 0 || e.target.value === "") {
                                            handleChange(e);
                                        }
                                    }}
                                    value={values.forceCourierNotificationDistance}
                                    min={0}
                                />
                            </Form.Item>
                        </>
                    )}

                    <Form.Item
                        label={t("fields.authorizedPerson", {ns: "common"})}
                        validateStatus={
                            getIn(errors, `authorizedPerson`) && getIn(touched, `authorizedPerson`)
                                ? "error"
                                : "success"
                        }
                        help={
                            getIn(errors, `authorizedPerson`) && getIn(touched, `authorizedPerson`)
                                ? getIn(errors, `authorizedPerson`)
                                : null
                        }
                    >
                        <Input
                            name={"authorizedPerson"}
                            value={values.authorizedPerson}
                            onChange={handleChange}
                        />
                    </Form.Item>
                    <Row type="flex" justify="space-between">
                        <Col span="11">
                            <Form.Item
                                label={t("fields.authorizedPhone", {ns: "common"})}
                                validateStatus={
                                    getIn(errors, `authorizedPhone`) &&
                                    getIn(touched, `authorizedPhone`)
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    getIn(errors, `authorizedPhone`) &&
                                    getIn(touched, `authorizedPhone`)
                                        ? getIn(errors, `authorizedPhone`)
                                        : null
                                }
                            >
                                <MaskedInput
                                    placeholder="0(555)-555-5555"
                                    mask="1(111)-111-1111"
                                    name={"authorizedPhone"}
                                    value={values.authorizedPhone}
                                    onChange={handleChange}
                                />
                            </Form.Item>
                        </Col>
                        <Col span="11">
                            <Form.Item
                                label={t("fields.authorizedEmail", {ns: "common"})}
                                validateStatus={
                                    getIn(errors, `authorizedEmail`) &&
                                    getIn(touched, `authorizedEmail`)
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    getIn(errors, `authorizedEmail`) &&
                                    getIn(touched, `authorizedEmail`)
                                        ? getIn(errors, `authorizedEmail`)
                                        : null
                                }
                            >
                                <Input
                                    name={"authorizedEmail"}
                                    onChange={handleChange}
                                    value={values.authorizedEmail}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row type="flex" justify="space-between">
                        <Col span="24">
                            <AuthorizedIDForm
                                subCompany={subCompany?.data}
                                loading={loading}
                                refetch={() =>
                                    history.push("/companies/sub-company/" + subCompanyId)
                                }
                                okButtonText={"Doğrula ve Kaydet"}
                            />
                        </Col>
                    </Row>
                    <Form.Item
                        label={t("fields.address", {ns: "common"})}
                        validateStatus={
                            getIn(errors, `address`) && getIn(touched, `address`)
                                ? "error"
                                : "success"
                        }
                        help={
                            getIn(errors, `address`) && getIn(touched, `address`)
                                ? getIn(errors, `address`)
                                : null
                        }
                    >
                        <Input.TextArea
                            name={`address`}
                            value={values.address}
                            onChange={handleChange}
                        />
                    </Form.Item>

                    <Form.Item
                        label={t("fields.uniqueKey", {ns: "common"})}
                        validateStatus={
                            getIn(errors, `uniqueKey`) && getIn(touched, `uniqueKey`)
                                ? "error"
                                : "success"
                        }
                        help={
                            getIn(errors, `uniqueKey`) && getIn(touched, `uniqueKey`)
                                ? getIn(errors, `uniqueKey`)
                                : null
                        }
                    >
                        <Input
                            name={"uniqueKey"}
                            onChange={handleChange}
                            value={values.uniqueKey}
                        />
                    </Form.Item>
                    {stores?.meStore?.checkRole("isAdmin") && (
                        <Form.Item
                            label={"Para Transfer Kodu"}
                            validateStatus={
                                getIn(errors, `expCode`) && getIn(touched, `expCode`)
                                    ? "error"
                                    : "success"
                            }
                            help={
                                getIn(errors, `expCode`) && getIn(touched, `expCode`)
                                    ? getIn(errors, `expCode`)
                                    : null
                            }
                        >
                            <Input
                                name={"expCode"}
                                onChange={handleChange}
                                value={values.expCode}
                            />
                        </Form.Item>
                    )}
                    <Form.Item label="External ID">
                        <Input
                            name={"externalId"}
                            onChange={handleChange}
                            value={values.externalId}
                        />
                    </Form.Item>

                    <div style={{display: "flex", gap: "50px", alignItems: "center"}}>
                        <div style={{display: "flex", alignItems: "center", marginBottom: "15px"}}>
                            <span style={{fontWeight: "bold"}}>Kredili Hale Getir:</span>
                            <Switch
                                checked={creditSwitch}
                                onChange={checked => setCreditSwitch(checked)}
                                style={{marginLeft: "15px"}}
                            />
                        </div>
                        <div>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    marginBottom: "15px"
                                }}
                            >
                                <span style={{fontWeight: "bold", marginRight: "15px"}}>
                                    Alt Firma Çalışma şekli:{" "}
                                </span>
                                <span>Dedike</span>
                                <Switch
                                    checked={poolSwitch}
                                    onChange={checked => {
                                        setPoolSwitch(checked);
                                    }}
                                    style={{marginLeft: "15px"}}
                                />
                                <span style={{marginLeft: "15px"}}>Havuz</span>
                            </div>
                        </div>
                        <div style={{display: "flex", alignItems: "center", marginBottom: "15px"}}>
                            <span style={{fontWeight: "bold"}}>Smart Locker:</span>
                            <Switch
                                checked={smartLockerSwitch}
                                onChange={checked => setSmartLockerSwitch(checked)}
                                style={{marginLeft: "15px"}}
                            />
                        </div>
                        <div style={{display: "flex", alignItems: "center", marginBottom: "15px"}}>
                            <span style={{fontWeight: "bold"}}>Manuel Atama:</span>
                            <Switch
                                checked={isAllocateClosed}
                                onChange={checked => setIsAllocateClosed(checked)}
                                style={{marginLeft: "15px"}}
                            />
                        </div>
                        <div style={{display: "flex", alignItems: "center", marginBottom: "15px"}}>
                            <span style={{fontWeight: "bold"}}>
                                {t(`fields.testUser`, {ns: "common"})}
                            </span>
                            <Switch
                                checked={isTestUser}
                                onChange={checked => setIsTestUser(checked)}
                                style={{marginLeft: "15px"}}
                            />
                        </div>
                    </div>
                    <Form.Item
                        label={t(`integrations.commonFields.allowedOrderProviders`, {
                            ns: "integrations"
                        })}
                    >
                        <TagInput
                            tags={tags}
                            setTags={setTags}
                            addTitle={t(`integrations.commonFields.addAllowedOrderProvider`, {
                                ns: "integrations"
                            })}
                            addPlaceHolderText={t(
                                `integrations.commonFields.allowedOrderProvidersPlaceholder`,
                                {
                                    ns: "integrations"
                                }
                            )}
                        ></TagInput>
                    </Form.Item>
                    <Form.Item
                        label={t("fields.location", {ns: "common"})}
                        validateStatus={
                            getIn(errors, `location`) && getIn(touched, `location`)
                                ? "error"
                                : "success"
                        }
                        help={
                            getIn(errors, `location`) && getIn(touched, `location`)
                                ? getIn(errors, `location`)
                                : null
                        }
                    >
                        <Row type="flex" gutter={16} justify="center">
                            <Col>{`${t("fields.latitude", {
                                ns: "common"
                            })}: ${values.location?.latitude.toFixed(5)}`}</Col>
                            <Col>{`${t("fields.longitude", {
                                ns: "common"
                            })}: ${values.location?.longitude.toFixed(5)}`}</Col>
                        </Row>
                        <SingleMarkerMap
                            center={{
                                lat: values.location?.latitude,
                                lng: values.location?.longitude
                            }}
                            onClick={({lat, lng}) => {
                                const c = {
                                    longitude: lng,
                                    latitude: lat
                                };
                                setFieldValue(`location`, c);
                            }}
                            addExternalInput
                            icon={<BankOutlined />}
                        />
                    </Form.Item>
                    <Form.Item
                        label={t("fields.subCompanyCode", {ns: "common"})}
                        validateStatus={
                            getIn(errors, `subCompanyCode`) && getIn(touched, `subCompanyCode`)
                                ? "error"
                                : "success"
                        }
                        help={
                            getIn(errors, `subCompanyCode`) && getIn(touched, `subCompanyCode`)
                                ? getIn(errors, `subCompanyCode`)
                                : null
                        }
                    >
                        <Row>
                            <Input
                                disabled
                                value={`${companyCode}`}
                                style={{borderRightWidth: 1, width: 60}}
                            />
                            <Input
                                disabled
                                value={`${companyCityCode}`}
                                style={{borderLeftWidth: 0, width: 40}}
                            />
                            <Input
                                disabled={subCompany?.data?.subCompanyCode}
                                name={"subCompanyCode"}
                                data-testid="subCompanyCode"
                                value={values.subCompanyCode}
                                onChange={handleChange}
                                style={{borderLeftWidth: 0, width: 80}}
                            />
                        </Row>
                    </Form.Item>
                </FormikProvider>
                <Row type="flex">
                    <CustomButton type="primary" htmlType="submit" disabled={isSubmitting}>
                        {t("actions.save", {ns: "common"})}
                    </CustomButton>
                </Row>
            </Form>
        </Col>
    ));
};
